interface TryOnRequest {
  photoId: string;
  platformProductId: string;
  productTitle: string;
  productImageUrl: string;
  variantId?: string;
}

interface TryOnResponse {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  resultUrl?: string;
  errorMessage?: string;
}

interface Photo {
  id: string;
  storageUrl: string;
  originalFilename: string;
  width: number;
  height: number;
  createdAt: string;
}

interface TryOn {
  id: string;
  photoId: string;
  platformProductId: string;
  productTitle: string;
  productImageUrl: string;
  photoUrl: string;
  status: string;
  resultUrl: string | null;
  errorMessage: string | null;
  createdAt: string;
}

interface TryOnListResponse {
  tryons: TryOn[];
  total: number;
}

interface WidgetAvailabilityResponse {
  allowed: boolean;
  reason?: string | null;
  subscriptionStatus?: string | null;
  tryonButtonEnabled?: boolean;
}

type VisitorAuthMode = 'cookie' | 'header';

interface VisitorSessionTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

class FatalWebSocketError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FatalWebSocketError';
  }
}

export class SnapStyleAPIClient {
  private static readonly STORAGE_ACCESS_TOKEN_KEY = 'snapstyle:visitor_access_token';
  private static readonly STORAGE_REFRESH_TOKEN_KEY = 'snapstyle:visitor_refresh_token';
  private static readonly STORAGE_AUTH_MODE_KEY = 'snapstyle:visitor_auth_mode';

  private baseUrl: string;
  private apiKey: string;
  private authMode: VisitorAuthMode = 'cookie';
  private accessToken: string = '';
  private refreshToken: string = '';

  constructor(baseUrl: string, apiKey: string) {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
    this.loadStoredSession();
  }

  setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  private getLocalStorage(): Storage | null {
    try {
      if (typeof window === 'undefined') {
        return null;
      }
      return window.localStorage;
    } catch {
      return null;
    }
  }

  private canUseLocalStorage(): boolean {
    const storage = this.getLocalStorage();
    if (!storage) {
      return false;
    }

    try {
      const key = '__snapstyle_test__';
      storage.setItem(key, '1');
      storage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  private loadStoredSession(): boolean {
    const storage = this.getLocalStorage();
    if (!storage) {
      return false;
    }

    let accessToken = '';
    let refreshToken = '';
    let authMode: string | null = null;

    try {
      accessToken = storage.getItem(SnapStyleAPIClient.STORAGE_ACCESS_TOKEN_KEY) || '';
      refreshToken = storage.getItem(SnapStyleAPIClient.STORAGE_REFRESH_TOKEN_KEY) || '';
      authMode = storage.getItem(SnapStyleAPIClient.STORAGE_AUTH_MODE_KEY);
    } catch {
      return false;
    }

    if (!accessToken || !refreshToken || authMode !== 'header') {
      return false;
    }

    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.authMode = 'header';
    return true;
  }

  private hasInMemoryHeaderSession(): boolean {
    return (
      this.authMode === 'header' &&
      Boolean(this.accessToken) &&
      Boolean(this.refreshToken)
    );
  }

  private persistHeaderSession(tokens: VisitorSessionTokens): void {
    this.accessToken = tokens.accessToken;
    this.refreshToken = tokens.refreshToken;
    this.authMode = 'header';

    const storage = this.getLocalStorage();
    if (!storage || !this.canUseLocalStorage()) {
      return;
    }

    try {
      storage.setItem(SnapStyleAPIClient.STORAGE_ACCESS_TOKEN_KEY, tokens.accessToken);
      storage.setItem(SnapStyleAPIClient.STORAGE_REFRESH_TOKEN_KEY, tokens.refreshToken);
      storage.setItem(SnapStyleAPIClient.STORAGE_AUTH_MODE_KEY, 'header');
    } catch {
      // Keep the in-memory header session even if persistent storage is unavailable.
    }
  }

  private clearStoredSession(): void {
    this.accessToken = '';
    this.refreshToken = '';
    this.authMode = 'cookie';

    const storage = this.getLocalStorage();
    if (!storage) {
      return;
    }

    try {
      storage.removeItem(SnapStyleAPIClient.STORAGE_ACCESS_TOKEN_KEY);
      storage.removeItem(SnapStyleAPIClient.STORAGE_REFRESH_TOKEN_KEY);
      storage.removeItem(SnapStyleAPIClient.STORAGE_AUTH_MODE_KEY);
    } catch {
      // Session state was still cleared from memory.
    }
  }

  private setCookieSession(): void {
    this.accessToken = '';
    this.refreshToken = '';
    this.authMode = 'cookie';
    this.clearStoredSession();
  }

  private buildHeaders(headers?: HeadersInit, useRefreshToken = false): Headers {
    const normalized = new Headers(headers);

    if (this.authMode === 'header') {
      const token = useRefreshToken ? this.refreshToken : this.accessToken;
      if (token) {
        normalized.set('Authorization', `Bearer ${token}`);
      }
    }

    return normalized;
  }

  private async authenticatedFetch(
    url: string,
    options: RequestInit = {},
    maxRetries = 3,
    useRefreshToken = false,
    retryOnUnauthorized = true,
  ): Promise<Response> {
    const init: RequestInit = {
      ...options,
      credentials: options.credentials ?? 'include',
      headers: this.buildHeaders(options.headers, useRefreshToken),
    };

    const response = await this.fetchWithRetry(url, init, maxRetries);
    if (
      retryOnUnauthorized &&
      response.status === 401 &&
      this.authMode === 'header' &&
      !useRefreshToken &&
      this.refreshToken
    ) {
      const refreshed = await this.refreshVisitorSession();
      if (refreshed) {
        const retryInit: RequestInit = {
          ...options,
          credentials: options.credentials ?? 'include',
          headers: this.buildHeaders(options.headers, useRefreshToken),
        };
        return await this.fetchWithRetry(url, retryInit, maxRetries);
      }
    }

    return response;
  }

  private async validateCookieSession(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/auth/visitor/me`, {
        credentials: 'include',
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  private async refreshVisitorSession(): Promise<boolean> {
    if (!this.refreshToken) {
      return false;
    }

    try {
      const previousMode = this.authMode;
      this.authMode = 'header';

      const response = await this.fetchWithRetry(
        `${this.baseUrl}/api/v1/auth/visitor/refresh`,
        {
          method: 'POST',
          credentials: 'include',
          headers: this.buildHeaders({ 'Content-Type': 'application/json' }, true),
        },
      );

      if (!response.ok) {
        this.authMode = previousMode;
        return false;
      }

      const data = (await response.json()) as VisitorSessionTokens;
      this.persistHeaderSession(data);
      return true;
    } catch (error) {
      console.warn(
        '[SnapStyle] Failed to refresh visitor session:',
        error instanceof Error ? error.message : error,
      );
      return false;
    }
  }

  async ensureVisitorSession(): Promise<void> {
    const hasReusableHeaderSession =
      this.loadStoredSession() || this.hasInMemoryHeaderSession();

    if (hasReusableHeaderSession) {
      const response = await this.authenticatedFetch(
        `${this.baseUrl}/api/v1/auth/visitor/me`,
        { method: 'GET' },
        0,
      );

      if (response.ok) {
        return;
      }

      if (response.status !== 401 && response.status !== 403) {
        return;
      }

      this.clearStoredSession();
    }

    await this.createGuestSession();
  }

  /**
   * Wraps fetch() with automatic retries for transient network errors.
   * Only retries on TypeError (network failures like ERR_NETWORK_CHANGED),
   * NOT on HTTP error responses (4xx/5xx).
   */
  private async fetchWithRetry(
    url: string,
    options?: RequestInit,
    maxRetries = 3,
  ): Promise<Response> {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fetch(url, options);
      } catch (error) {
        // Only retry on network-level failures (TypeError: Failed to fetch)
        if (attempt === maxRetries || !(error instanceof TypeError)) {
          throw error;
        }
        const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
        console.warn(
          `[SnapStyle] Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`,
        );
        await new Promise((r) => setTimeout(r, delay));
      }
    }
    // Unreachable, but satisfies TypeScript
    throw new Error('Max retries exceeded');
  }

  async createGuestSession(): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/auth/visitor/guest`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 409) {
        // Session already exists, safe to proceed
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to create guest session: ${response.status} ${response.statusText}`);
      }

      const session = (await response.json()) as VisitorSessionTokens;
      const cookiesWorked = await this.validateCookieSession();

      if (cookiesWorked) {
        this.setCookieSession();
        return;
      }

      this.persistHeaderSession(session);
    } catch (error) {
      if (error instanceof TypeError) {
        throw new Error('Network error: Please check your internet connection and CORS configuration');
      }
      throw error;
    }
  }

  async uploadPhoto(file: File): Promise<Photo> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.authenticatedFetch(`${this.baseUrl}/api/v1/photos`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to upload photo');
    }

    return await response.json();
  }

  async listPhotos(): Promise<Photo[]> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/api/v1/photos`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch photos');
    }

    const data = await response.json();
    return data.photos;
  }

  async createTryOn(request: TryOnRequest): Promise<TryOnResponse> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/api/v1/tryon`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Store-API-Key': this.apiKey },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error('Failed to create try-on');
    }

    return await response.json();
  }

  async getWidgetAvailability(): Promise<WidgetAvailabilityResponse> {
    if (!this.apiKey) {
      throw new Error("Missing store API key");
    }

    const response = await this.fetchWithRetry(`${this.baseUrl}/api/v1/public/widget/availability`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-Store-API-Key': this.apiKey,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch widget availability: ${response.status}`);
    }

    return await response.json();
  }

  async getTryOn(tryonId: string): Promise<TryOnResponse> {
    const response = await this.authenticatedFetch(
      `${this.baseUrl}/api/v1/tryon/${tryonId}`,
      { credentials: 'include' },
    );

    if (!response.ok) {
      throw new Error('Failed to fetch try-on');
    }

    return await response.json();
  }

  /**
   * Wait for a try-on to reach a terminal status using WebSocket (preferred)
   * with automatic fallback to HTTP polling.
   */
  async waitForTryOn(tryonId: string): Promise<TryOnResponse> {
    if (this.authMode === 'header') {
      return await this.pollTryOn(tryonId);
    }

    try {
      return await this._waitViaWebSocket(tryonId);
    } catch (error) {
      if (error instanceof FatalWebSocketError) {
        throw error;
      }
      console.warn(
        '[SnapStyle] WebSocket unavailable, falling back to polling:',
        error instanceof Error ? error.message : error,
      );
      return await this.pollTryOn(tryonId);
    }
  }

  /**
   * Internal: wait for try-on status via WebSocket connection.
   * Rejects if the WebSocket cannot be established within 5 seconds.
   */
  private _waitViaWebSocket(tryonId: string): Promise<TryOnResponse> {
    return new Promise((resolve, reject) => {
      const wsUrl = this._buildWsUrl(`/api/v1/ws/tryon/${tryonId}`);
      let settled = false;

      const ws = new WebSocket(wsUrl);

      // If we can't even connect within 5s, reject so the caller falls back
      const connectTimeout = setTimeout(() => {
        if (!settled) {
          settled = true;
          ws.close();
          reject(new Error('WebSocket connection timeout'));
        }
      }, 5000);

      ws.onopen = () => {
        clearTimeout(connectTimeout);
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);

          // Ignore heartbeat pings
          if (data.type === 'ping') return;

          if (data.status === 'completed' || data.status === 'failed') {
            if (!settled) {
              settled = true;
              // Let the server close the connection after sending the final status
              resolve(data as TryOnResponse);
            }
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onerror = () => {
        clearTimeout(connectTimeout);
        if (!settled) {
          settled = true;
          reject(new Error('WebSocket connection failed'));
        }
      };

      ws.onclose = (event: CloseEvent) => {
        clearTimeout(connectTimeout);
        if (!settled) {
          settled = true;
          // 4001 = auth failure, 4004 = not found — don't fallback, reject clearly
          if (event.code === 4001 || event.code === 4004) {
            reject(new FatalWebSocketError(event.reason || 'WebSocket closed'));
          } else {
            reject(new Error(`WebSocket closed unexpectedly (code: ${event.code})`));
          }
        }
      };
    });
  }

  /**
   * Internal fallback: poll GET /tryon/{id} until terminal status.
   */
  private async pollTryOn(tryonId: string, maxAttempts = 60): Promise<TryOnResponse> {
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;

    for (let i = 0; i < maxAttempts; i++) {
      try {
        const tryon = await this.getTryOn(tryonId);
        consecutiveErrors = 0; // Reset on success

        if (tryon.status === 'completed' || tryon.status === 'failed') {
          return tryon;
        }
      } catch (error) {
        consecutiveErrors++;
        console.warn(
          `[SnapStyle] Poll attempt ${i + 1} failed (${consecutiveErrors} consecutive):`,
          error instanceof Error ? error.message : error,
        );

        // Give up if too many consecutive failures (persistent network issue)
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          throw new Error(
            'Try-on polling failed: persistent network issue. Please check your connection and try again.',
          );
        }
      }

      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    throw new Error('Try-on timeout');
  }

  /**
   * Derive a WebSocket URL from the HTTP baseUrl.
   * https://example.com → wss://example.com/path
   * http://localhost:8000 → ws://localhost:8000/path
   */
  private _buildWsUrl(path: string): string {
    const url = new URL(this.baseUrl);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${url.origin}${path}`;
  }

  async listTryOns(limit = 20, offset = 0, status = 'completed'): Promise<TryOnListResponse> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/api/v1/tryon?limit=${limit}&offset=${offset}&status=${status}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch try-ons');
    }

    return await response.json();
  }

  async deletePhoto(photoId: string): Promise<void> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/api/v1/photos/${photoId}`, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to delete photo');
    }
  }
}
